import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatBRL, formatBrDate } from "@/lib/format";
import { ProductLink } from "../misc/ProductLink";
import { BadgeSeguradora, STATUS_APOLICE, STATUS_PROPOSTA, StatusBadge } from "./comuns";
import type { ApoliceV, PropostaV } from "./seguradoras";
import { buscarPorSegurado } from "./useTabelaSeguros";

/**
 * O que este contato tem de SEGURO, mostrado dentro da ficha do contato.
 *
 * O contato é compartilhado entre os dois produtos, então cada módulo precisa
 * mostrar o que o outro tem — senão quem está no consórcio nunca descobre que
 * o cliente também tem seguro.
 *
 * A ligação é por CPF: é o único campo em comum entre contacts.details->>'cpf'
 * e `segurado_cpf_digits` nas views. Sem CPF no contato, não há como cruzar —
 * e o card some em vez de fingir que a pessoa não tem seguro.
 *
 * ATENÇÃO à leitura vazia: `seguros_*` é restrito a super admin. Pra quem não
 * é, a RLS filtra e este card simplesmente não aparece — não é erro.
 */

type Apolice = Pick<ApoliceV, "seguradora" | "numero" | "status" | "premio_mensal" | "inicio_vigencia" | "url">;
type Proposta = Pick<PropostaV, "seguradora" | "numero" | "status" | "premio_mensal" | "data_protocolo" | "url">;

export const SegurosDoContato = ({ cpf }: { cpf: string | null | undefined }) => {
  const digitos = (cpf ?? "").replace(/\D/g, "");
  const [apolices, setApolices] = useState<Apolice[]>([]);
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (digitos.length !== 11) {
      setCarregando(false);
      return;
    }
    let ativo = true;
    (async () => {
      const [ap, pr] = await Promise.all([
        buscarPorSegurado<Apolice>(
          "seguros_apolices_v",
          "seguradora, numero, status, premio_mensal, inicio_vigencia, url",
          digitos,
          { coluna: "inicio_vigencia" },
        ),
        buscarPorSegurado<Proposta>(
          "seguros_propostas_v",
          "seguradora, numero, status, premio_mensal, data_protocolo, url",
          digitos,
          { coluna: "data_protocolo" },
        ),
      ]);
      if (!ativo) return;
      setApolices(ap);
      setPropostas(pr);
      setCarregando(false);
    })();
    return () => {
      ativo = false;
    };
  }, [digitos]);

  // Sem nada pra mostrar, o card some: espaço morto dizendo "não tem seguro"
  // é pior que ausência, e a maioria dos contatos não tem.
  if (carregando || (apolices.length === 0 && propostas.length === 0)) return null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldCheck className="h-5 w-5 text-primary" />
          Seguros
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {apolices.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase text-muted-foreground">Apólices</p>
            {apolices.map((a) => (
              <div key={`${a.seguradora}:${a.numero}`} className="rounded border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2 font-medium">
                    Apólice {a.numero}
                    <BadgeSeguradora codigo={a.seguradora} />
                  </span>
                  <StatusBadge status={a.status} mapa={STATUS_APOLICE} />
                </div>
                <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                  <span>
                    <strong className="text-foreground">Prêmio:</strong> {formatBRL(Number(a.premio_mensal || 0))}/mês
                  </span>
                  <span>
                    <strong className="text-foreground">Início:</strong> {formatBrDate(a.inicio_vigencia)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {propostas.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase text-muted-foreground">Propostas</p>
            {propostas.map((p) => (
              <div key={`${p.seguradora}:${p.numero}`} className="rounded border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm">
                    {formatBrDate(p.data_protocolo)}
                    <BadgeSeguradora codigo={p.seguradora} />
                  </span>
                  <StatusBadge status={p.status} mapa={STATUS_PROPOSTA} />
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  <strong className="text-foreground">Prêmio:</strong> {formatBRL(Number(p.premio_mensal || 0))}/mês
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Fora do módulo de Seguros, isto troca de origem (ver ProductLink). */}
        <ProductLink
          module="insurance"
          to={`/seguros/apolices?q=${digitos}`}
          className="inline-block text-xs text-primary underline"
        >
          Abrir em Seguros
        </ProductLink>
      </CardContent>
    </Card>
  );
};
